import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useStockAlertDetails, StockAlertProduct } from "@/hooks/notifications/useStockAlertDetails";
import { useDiscrepancyReports } from "@/hooks/notifications/useDiscrepancyCount";
import { useAuth } from "@/context/AuthContext";
import { Package, Warehouse, MapPin, Ruler, BarChart3, Clock, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { StockDiscrepancyDialog } from "./StockDiscrepancyDialog";

interface StockAlertDialogProps {
  notificationId: string;
  voNumber: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const StockProductCard = ({ 
  product, 
  notificationId,
  onReportDiscrepancy 
}: { 
  product: StockAlertProduct;
  notificationId: string;
  onReportDiscrepancy: (product: StockAlertProduct) => void;
}) => {
  return (
    <div className="border rounded-lg p-4 space-y-3 bg-card hover:bg-accent/5 transition-colors">
      <div className="space-y-1">
        <h4 className="font-semibold text-foreground text-2xl">{product.product_name}</h4>
        <div className="flex flex-col gap-1 text-xl text-muted-foreground">
          {product.product_sku && <p>SKU: {product.product_sku}</p>}
          {product.product_bc && <p>Barcode: {product.product_bc}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t">
        <div className="flex items-center gap-2 text-xl">
          <Package className="h-6 w-6 text-primary" />
          <span className="font-medium">{product.current_stock} stuks</span>
        </div>
        
        <div className="flex items-center gap-2 text-xl">
          <Warehouse className="h-6 w-6 text-primary" />
          <span>{product.magazijn_label}</span>
        </div>
        
        <div className="flex items-center gap-2 text-xl">
          <MapPin className="h-6 w-6 text-primary" />
          <span>Locatie: <span className="font-semibold">{product.locatie}</span></span>
        </div>
        
        <div className="flex items-center gap-2 text-xl">
          <Ruler className="h-6 w-6 text-primary" />
          <span>{product.eenheid}</span>
        </div>
        
        {product.moq && (
          <div className="flex items-center gap-2 text-xl">
            <BarChart3 className="h-6 w-6 text-primary" />
            <span>MOQ: {product.moq}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t text-lg text-muted-foreground">
        <div className="flex items-center gap-1">
          <Package className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-foreground text-xl">Besteld: {product.product_quantity || '-'} stuks</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="h-5 w-5" />
          <span>{format(new Date(product.last_check), "d MMM yyyy, HH:mm", { locale: nl })}</span>
        </div>
      </div>

      <Button
        variant="outline"
        className="w-full mt-3 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={() => onReportDiscrepancy(product)}
      >
        <AlertTriangle className="h-4 w-4 mr-2" />
        Melding voorraadverschil
      </Button>
    </div>
  );
};

export const StockAlertDialog = ({
  notificationId,
  voNumber,
  open,
  onOpenChange,
}: StockAlertDialogProps) => {
  const { data: products, isLoading, error } = useStockAlertDetails(notificationId);
  const [selectedProduct, setSelectedProduct] = useState<StockAlertProduct | null>(null);
  const [discrepancyDialogOpen, setDiscrepancyDialogOpen] = useState(false);
  const { user } = useAuth();
  const { data: previousReports } = useDiscrepancyReports(
    selectedProduct?.id,
    selectedProduct?.product_sku || undefined
  );

  const handleReportDiscrepancy = (product: StockAlertProduct) => {
    setSelectedProduct(product);
    setDiscrepancyDialogOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl">
            Producten op voorraad {voNumber && `- ${voNumber}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading && (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="border rounded-lg p-4 space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <div className="grid grid-cols-2 gap-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-destructive">
              <p>Fout bij ophalen voorraadinfo</p>
              <p className="text-xl text-muted-foreground mt-1">{error.message}</p>
            </div>
          )}

          {!isLoading && !error && products && products.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="h-18 w-18 mx-auto mb-2 opacity-50" />
              <p>Geen voorraadgegevens beschikbaar</p>
            </div>
          )}

          {!isLoading && !error && products && products.length > 0 && (
            <>
              <div className="space-y-3">
                {products.map((product) => (
                  <StockProductCard 
                    key={product.id} 
                    product={product}
                    notificationId={notificationId}
                    onReportDiscrepancy={handleReportDiscrepancy}
                  />
                ))}
              </div>
              
              <div className="text-center text-xl text-muted-foreground pt-2 border-t">
                {products.length} {products.length === 1 ? 'product' : 'producten'} op voorraad
              </div>
            </>
          )}
        </div>
      </DialogContent>

      {selectedProduct && (
        <StockDiscrepancyDialog
          open={discrepancyDialogOpen}
          onOpenChange={setDiscrepancyDialogOpen}
          notificationId={notificationId}
          monitoringId={selectedProduct.id}
          productName={selectedProduct.product_name}
          productSku={selectedProduct.product_sku || undefined}
          productBc={selectedProduct.product_bc || undefined}
          currentStock={selectedProduct.current_stock}
          reportedBy={user?.id}
          reportedByName={user?.name || user?.username}
          previousReports={previousReports}
        />
      )}
    </Dialog>
  );
};
